import { AspectRatio, Box, Flex, Skeleton, Text } from '@chakra-ui/react';
import Button from '@components/interactive/Button';
import Header from '@components/typography/Header';
import Quote from '@components/typography/Quote';
import pauseIcon from '@public/assets/icons/pause-solid.svg';
import playIcon from '@public/assets/icons/play-solid.svg';
import { FsmColors } from 'chakra-ui/fsmTheme';
import Image from 'next/image';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { VideoState } from '.';

export enum AvailabilityTag {
  AVAILABLE_TODAY = 'AVAILABLE_TODAY',
  AVAILABLE_TOMORROW = 'AVAILABLE_TOMORROW',
  AVAILABLE_LATER = 'AVAILABLE_LATER',
}

const shouldShowTag = (availabilityTag: AvailabilityTag) => {
  return [AvailabilityTag.AVAILABLE_TODAY, AvailabilityTag.AVAILABLE_TOMORROW].includes(availabilityTag);
};

export const AvailabilityText = {
  [AvailabilityTag.AVAILABLE_TODAY]: 'Available Today',
  [AvailabilityTag.AVAILABLE_TOMORROW]: 'Available Tomorrow',
};

interface CardProps {
  index: number;
  borderColor: string;
  agent: any;
  playingIndex: number;
  setPlayingIndex: (index: number) => void;
  onButtonClick?: (index: number) => void;
  video: boolean;
}

const Card = (props: CardProps) => {
  const posthog = usePostHog();
  const { index, borderColor, agent, onButtonClick, video, playingIndex, setPlayingIndex, ...rest } = props;

  const [imageLoaded, setImageLoaded] = useState(false);
  const [videoState, setVideoState] = useState(VideoState.NOT_STARTED);

  const play = (index: number) => {
    setPlayingIndex(index);
    setVideoState(VideoState.PLAYING);
    posthog.capture('Play Agent Video', { agent });
  };

  const pause = () => {
    setVideoState(VideoState.PAUSED);
    posthog.capture('Pause Agent Video', { agent });
  };

  // if video is not the current playing video, set to NOT_STARTED.
  // for example, user clicks on an avatar while a video is playing.
  useEffect(() => {
    if (index !== playingIndex) {
      setVideoState(VideoState.NOT_STARTED);
    }
  }, [index, playingIndex]);

  const shortBio = `${agent.info.bio.split('.').slice(0, 2)}.`;
  const color = borderColor || FsmColors.blue;
  const showTag = shouldShowTag(agent.availabilityTag);
  const hasVideo = agent.info.videoUrl;

  return (
    <Flex
      key={index}
      border={`10px solid ${color}`}
      w="100%"
      bg={FsmColors.white}
      minW="80%"
      mx={{ base: 2, lg: 0 }}
      direction={{ base: 'column', md: 'row' }}
      {...rest}
    >
      <Box position="relative" borderBottom="none" bg={borderColor}>
        {videoState === VideoState.NOT_STARTED ? (
          <Flex
            zIndex={2}
            position="absolute"
            w="100%"
            alignItems="center"
            justifyContent="space-between"
            bottom={{ base: 12, md: 0 }}
            left={0}
            p={5}
          >
            <Header as="h4" color="white">
              {agent.info.name}
            </Header>
          </Flex>
        ) : null}
        <AspectRatio bg={borderColor} position="relative" w={{ base: '100%', md: 500 }} ratio={1}>
          {videoState !== VideoState.NOT_STARTED ? (
            <ReactPlayer
              controls
              top={0}
              left={0}
              position="absolute"
              height="100%"
              width="100%"
              playing={index === playingIndex && videoState === VideoState.PLAYING}
              url={agent.info.videoUrl}
            />
          ) : (
            <Skeleton isLoaded={imageLoaded}>
              <Image
                onLoadingComplete={() => {
                  setImageLoaded(true);
                }}
                alt={agent.info.name}
                src={agent.info.image}
                fill
              />
            </Skeleton>
          )}
        </AspectRatio>
        {hasVideo ? (
          <Button
            zIndex={2}
            w="100%"
            bg={borderColor || FsmColors.blue}
            borderRadius={0}
            opacity={videoState === VideoState.PLAYING ? 0 : 1}
            border="none"
            _hover={{
              fontSize: 24,
              opacity: 1,
            }}
            _active={{
              bg: borderColor,
            }}
            _focus={{
              bg: borderColor,
            }}
            justifyContent="flex-end"
            position="absolute"
            onClick={
              videoState === VideoState.PLAYING
                ? () => {
                    pause();
                  }
                : () => {
                    play(index);
                  }
            }
            top={0}
            color={FsmColors.white}
            right={0}
            fontSize={18}
            p={5}
          >
            {videoState === VideoState.PLAYING ? (
              <Box>
                <Image alt="Pause agent video" src={pauseIcon} height={20} width={20} />
              </Box>
            ) : (
              <Box>
                <Image alt="Play agent video" src={playIcon} height={20} width={20} />
              </Box>
            )}
            <Text color="white">Video</Text>
          </Button>
        ) : null}
        {videoState !== VideoState.PLAYING ? (
          <Flex
            zIndex={1}
            position="absolute"
            w="100%"
            h="100%"
            bg="linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.7) 90%);"
            justifyContent="space-between"
            bottom={0}
            left={0}
            p={5}
          />
        ) : null}
        {videoState === VideoState.NOT_STARTED &&
        [AvailabilityTag.AVAILABLE_TODAY, AvailabilityTag.AVAILABLE_TOMORROW].includes(agent.availabilityTag) ? (
          <Box
            zIndex={2}
            p={2}
            m={5}
            bottom={0}
            left={{ base: 0, md: 'unset' }}
            right={{ base: 'unset', md: 0 }}
            position="absolute"
            borderRadius={20}
            textAlign="center"
            bg={FsmColors.highlight}
            color={FsmColors.white}
          >
            <Text textAlign="center" fontWeight="bold" color={FsmColors.black} fontSize={{ base: 12, md: 15 }}>
              {AvailabilityText[agent.availabilityTag]}
            </Text>
          </Box>
        ) : null}
      </Box>
      <Flex
        bg={FsmColors.white}
        p={5}
        borderLeft={{ base: 'none', md: `10px solid ${color}` }}
        borderTop="none"
        direction="column"
      >
        <Button
          id="book-with-button"
          color="white"
          mb={5}
          bg={FsmColors.red}
          borderColor={FsmColors.red}
          onClick={() => {
            onButtonClick(index);
          }}
        >
          Schedule
        </Button>
        <Quote
          mb={showTag ? 5 : 0}
          minHeight={[288, 288, 204]}
          fontSize={18}
          lineHeight={6}
          color="black"
          overflowY="scroll"
          sx={{
            '::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {shortBio}
        </Quote>
      </Flex>
    </Flex>
  );
};

export default Card;
